import type { Language } from '@/i18n/dictionary';
import { LANGUAGE } from '@/i18n/dictionary';

import { forDisplay } from './displayName';

/**
 * Every string the device-enrolment flow renders, in one place.
 *
 * ## Why these are literals and not dictionary keys
 *
 * [decision 0226] — the nine device-enrolment states are **English only**: they are staff-facing, not part
 * of the candidate journey, and 0026's bilingual policy governs the candidate's screens. ⛔ **No key enters
 * the EN/ES dictionary**, so the 90% coverage floor is neither weakened nor waived — there is nothing here
 * to be a percentage of. The convention skill § 2's "no user-facing string literal in a component" yields to
 * an approved decision, which is the precedence its own § 1 sets out.
 *
 * ## Where they come from
 *
 * ⭐ **Read from the live Figma file — 01 through 06 on 2026-09-16, 07 through 09 on 2026-09-16 for
 * [KAD-6d] — from each node's `characters` and NEVER from its layer `name`.** Sections
 * `SETUP — Device enrolment (PIN)` (`12055:12971`) and `07 · Name this kiosk` (`12307:35603`) /
 * `08 · Name already taken` (`12307:35604`) / `09 · Ready` (`12055:12979`), at 1440 · 820 · 375.
 *
 * The two are not the same on these frames: the error copy on 03, 04 and 05 sits in a layer NAMED *"We use
 * this complete set of details together to confirm your identity…"*, which is another screen's sentence
 * entirely — and on 07, 08 and 09 the SAME layer name sits over *"You can rename it later from the Admin."*
 * and over the Ready summary line. A build made from layer names ships neither screen's copy.
 *
 * ⛔ **`Action link →` is NOT here.** It appears three times on each of 03, 04 and 05 — nine instances,
 * every one `visible: false`. They are the design system's slot defaults, not copy [decision 0225], and a
 * frame-faithful build that ships one ships placeholder content [AC-8].
 *
 * ⚠️ **Byte for byte.** `didn't` uses a straight apostrophe and `office's` a typographic one — that is what
 * the file draws, in the same section, and it is not ours to normalise. So are the em dashes, the `…` and
 * the `→`.
 */
/**
 * ⭐ **The language every literal in this file is written in** — English, by [decision 0226].
 *
 * It exists so the ONE place that has to declare it can cite the decision instead of hardcoding `'en'`
 * next to a comment: `App` sets `document.documentElement.lang` from the screen it is actually rendering,
 * and while the device is not yet the candidate's that screen is one of these [03-us-kad-1e AC-14, WCAG
 * 3.1.1]. ⛔ **It is not a translation seam** — nothing here is keyed, and widening [0226] is a Ground
 * Truth decision, not an edit.
 */
export const ENROLMENT_COPY_LANGUAGE: Language = LANGUAGE.en;

export const ENROLMENT_COPY = {
  /** Frames 01–04. The eyebrow, heading and body render on all four; the note on all four as well. */
  eyebrow: 'DEVICE SETUP',
  heading: 'Enter the office PIN',
  body: "Six digits, from Kiosks & devices in the Admin. This tablet will take on that office's jobs, language and screening.",
  /**
   * ⭐ The Ground Truth carried this as a LAYER NAME (`No sign-in note`) and never as copy until 2026-09-14.
   * It is real rendered text on 01, 02, 03 and 04 — and on none of 05 / 06 — which is decision 0090's
   * "no Microsoft 365 sign-in" drawn on exactly the states where a staffer would look for one.
   */
  noSignIn: 'No sign-in — the PIN is all this tablet needs.',

  /** Frame 03 · Error (2 left). ⛔ Identical whether the PIN belongs to another venue or to nowhere [0090]. */
  twoAttemptsLeft: "That PIN didn't work. Please try again — 2 attempts left.",
  /** Frame 04 · Error (1 left). */
  oneAttemptLeft: "That PIN didn't work. 1 attempt left before this device is locked.",

  /** Frame 05 · Locked. ⛔ No keypad, no retry, no timer: an admin is the only exit [AC-5]. */
  lockedHeading: 'Device locked',
  lockedBody:
    'Too many incorrect PINs. Ask a site admin to reset this device from the admin panel (Kiosks & devices).',
  lockedCallout:
    'This device is locked for security. A site admin can reset it under Kiosks & devices → Reset device.',

  /** Frame 06 · Connecting. The frame's content is a heading block and a spinner, and nothing else. */
  connectingEyebrow: 'CONNECTING',
  connectingHeading: 'Connecting this device…',
  connectingBody: 'Hang tight — this only takes a moment.',

  /**
   * Frames 07 · Name this kiosk and 08 · Name already taken. The eyebrow is `eyebrow` above — both frames
   * draw `DEVICE SETUP`, byte for byte, at all three widths.
   */
  nameHeading: 'Name this kiosk',
  nameBody:
    'This is how the tablet appears in the Admin, so a recruiter knows which one needs help. Use where it stands.',
  nameLabel: 'Kiosk name',
  namePlaceholder: 'e.g. Front desk',
  nameAction: 'Start taking candidates',

  /** Frame 09 · Ready. ⭐ The action is `Start`, not `Start taking candidates` — a different frame. */
  readyEyebrow: 'DEVICE READY',
  readyHeading: 'This device is ready',
  readyBody: 'It will show the welcome screen for candidates.',
  readyAction: 'Start',
} as const;

/**
 * The collision refusal, with the refused name in it [AC-3; `0149`].
 *
 * ⭐ **SHIPPED although its node is `visible: false`, and the evidence is that the slot is AUTHORED.** On
 * the *08* frames the `Validation message` slot renders this exact sentence with the colliding name in it,
 * while the SAME slot on *07* renders the library's generic `Error text`; the frame is named *Name already
 * taken* and its field is pre-filled `Front desk`. A slot carrying screen-specific copy on the screen that
 * needs it is not a slot default [`0225`], and this is the only copy the design writes for the api's 409.
 *
 * ⚠️ **Curly quotes, as drawn.** They are the file's, not ours to normalise.
 *
 * The parameter is the name that was SUBMITTED, never the field's current value: once the staffer starts
 * editing, the sentence has to keep naming the name that was actually refused.
 */
export function nameTakenMessage(name: string): string {
  // ⭐ `forDisplay` at the render boundary ONLY — an invisible bidi override in a pasted name would make
  // this sentence quote something other than the name that was refused. ⛔ The stored value is untouched.
  return `Another kiosk at this site is already called “${forDisplay(name)}”. Try a different name.`;
}

/**
 * The line under the field and its helper that tells the staffer how the name can be freed — on the
 * collision ONLY [03-us-kad-6p; KAD-6d AC-4's deferred clause, returned as new work by `0376`].
 *
 * ⭐ **0439's sentence, which is the frame's except ONE word.** The node `Admin removal direction (0376)` on
 * *08 · PIN · Name taken* (1440 · 820 · 375, read live 2026-10-08) says *"at this office"*; [0439] rules
 * *"at this site"* at every venue, offices and hiring events alike, and a decision beats a frame [0297]. One
 * string — the app needs no venue kind. The frame still saying *office* is a follow-up for Design.
 *
 * ⭐ **English only, as a literal**, like every other string in this file [0226] — no dictionary key, no
 * Spanish, no per-venue variant [KAD-6p AC-3].
 *
 * The parameter is the SUBMITTED name, the one `nameTakenMessage` quotes, so a case variant quotes the
 * spelling that was typed, never the stored one [AC-1b; `0374`].
 *
 * ⛔ **No name to quote, no line** [KAD-6p § *The invariant*, failure posture]: a sentence with an empty quote
 * tells the staffer nothing, so `undefined` and the screen renders nothing in its place.
 */
export function nameTakenDirection(name: string): string | undefined {
  // Quoted exactly as `nameTakenMessage` quotes it, so the two lines can never name it differently.
  const shown = forDisplay(name);
  if (shown.trim() === '') return undefined;
  return `“${shown}” is already used at this site. Ask an admin to remove the old kiosk, or choose another name.`;
}

/**
 * The helper the frames draw under the field — ⭐ **SHIPPED, on the collision state ONLY**
 * [Operator ruling, 2026-09-17].
 *
 * ⛔ **Both halves of that are the ruling, and neither is a derivation.** Frames *07* and *08* draw this
 * string **visible in both states**; the Operator placed it on the duplicate-name refusal and withheld it
 * from the resting screen, where a staffer has been given no name to change. The departure from *07* is
 * registered in `design-qa/known-departures.md`, dated, so it is not "corrected" back later.
 *
 * ⭐ **The venue is a PARAMETER, exactly as the refused name is.** The frames hardcode *Bethlehem Office*,
 * which is a sample in precisely the way *Front desk* is a sample name — and telling a staffer in Allentown
 * that a name was suggested from the kiosks at Bethlehem would be a defect of its own. It is the
 * `displayName` the connect reported, the same value *09 · Ready* names, and it goes through `forDisplay`
 * because it is server-supplied text.
 *
 * ⛔ **With no venue name, the first sentence is DROPPED rather than degraded.** *"already at ."* is worse
 * than saying less, and the sentence is entirely about the venue — there is no clause left once it is gone.
 * The second sentence stands on its own: it is guidance that needs no venue to be true.
 *
 * ⏳ **The wording is OWED TO DESIGN and is shipped as ruled** [KAD-6 § G11]. *"Suggested from…"* asserts
 * that the field was populated from the kiosks already at this venue, and ⛔ **nothing suggests anything**,
 * in either state: [0118] rules that nothing is auto-assigned and [0118] and [0149] both rule out a picker,
 * and no source of a venue's existing kiosk names exists to make it true. On the refusal it reads better
 * than it did on the resting screen — the staffer has just been told a name is taken — but the first clause
 * is still a claim the product does not implement. ⛔ **No replacement is invented here.**
 */
export function nameSuggestionHelper(venueName: string | undefined): string {
  const guidance = 'Change it to something staff would recognise — “Front desk”, “Lobby”.';

  return venueName === undefined
    ? guidance
    : `Suggested from the kiosks already at ${forDisplay(venueName)}. ${guidance}`;
}

/**
 * ⭐ **The four paths the design does not draw, RULED BY THE OPERATOR on 2026-09-16** — and one that is
 * still a placeholder.
 *
 * **Germán López, in a delivery session:** all four render **an inline error above the digits, in the error
 * treatment frames 03 and 04 already draw** — the same component, the same placement, different copy.
 * ⛔ **Nothing new is drawn.** The principle he stated is the reason the strings are this short:
 * ⭐ **simple, nothing disclosed, no extra information for an attacker.**
 *
 * | Path | What it says | Ruling |
 * | --- | --- | --- |
 * | layer 2's address backoff [0203] | `tooManyAttempts` | 0261 |
 * | the coarse floor's flat 429 [0231; 0243] | ⭐ **the same string** | 0262 |
 * | a failed or slow *Connecting*, and any answer the tablet cannot read | `havingIssues` | 0262 |
 * | a `401` returning a device to the pad mid-journey [0245] | `credentialRefused` | 0263 |
 *
 * ⛔ **Still never the *Device locked* screen** [AC-6], and never the candidate check-in throttle's
 * `E2`/`E2b` — a different control with a different actor [0063].

 */
export const RULED_COPY = {
  /**
   * ⭐ **ONE string for BOTH 429s, and that is the requirement rather than a saving** [0261; 0262].
   *
   * Layer 2 counts failed PIN attempts against an address and the coarse floor counts REQUESTS — two
   * controls, two counters, told apart internally by the `outcome` field the floor does not carry. ⛔ **On
   * screen they must be indistinguishable**: which control refused is exactly the extra information the
   * Operator's *nothing disclosed* principle withholds. Two constants with the same value would drift apart
   * the first time somebody edited one, so there is one.
   *
   * ⛔ No countdown, no duration, no mention of an address. `retryAfterSeconds` is not even read into the
   * app.
   */
  tooManyAttempts: 'Too many attempts. Try again later.',
  /**
   * A failed or slow *Connecting*, a 503, a timeout, a body that is not the contract, or a layer-1 token
   * that could not be issued [AC-1b; AC-7].
   *
   * ⛔ It claims **no enrolment outcome**: it does not say the PIN was wrong and it does not say the device
   * is locked [§ *The invariant*, failure posture].
   */
  havingIssues: 'We are having some issues. Try again later.',
  /**
   * A `401` on a candidate-journey call, which returns the device to the pad [0245], and a wrong PIN whose
   * attempt the server counted against nothing.
   *
   * ⚠️ **The Operator's wording, and a concern is filed against it** [0263 § *The concern*]. A 401
   * mid-journey means the credential expired or was revoked — **the staffer typed nothing**, so *Incorrect
   * PIN* is non-disclosing and accurate about nothing: it invites a re-type of a PIN that was never wrong.
   * ⛔ **Build the ruling, not a substitute.** It sits behind this one name precisely so that swapping it is
   * a one-line change the moment the Operator answers.
   */
  credentialRefused: 'Incorrect PIN',
} as const;

/**
 * ⭐ **The strings NO frame draws, and that an accessibility criterion nevertheless requires**, written here
 * rather than in a component so that they are findable and so that replacing one is a one-line change.
 *
 * ⚠️ **The bucket spans more than one story, and its scope line says so rather than naming three screens**
 * — it read *"the one string on these three screens"* until [03-us-kad-6i] added a fourth from a different
 * story, and a bucket whose header contradicts its contents stops being findable, which is the single
 * property this header exists to protect [decision `0307` § *The one residual*].
 *
 * ⏳ **Neither string is RULED.** Both are recorded for the Operator in the pull request that ships them,
 * and both are bound by the same clause: ⛔ **nothing identifying** — no `kioskId`, no venue or
 * `displayName`, no credential, nothing about a PIN.
 */
export const RECORDED_COPY = {
  /**
   * The name is **required** — [KAD-6d] AC-1 says so and labels it as the spec's own inference, because the
   * `*` marker on *07* is `visible: false`. A nameless kiosk defeats the three staff surfaces that render
   * the name [`0118`], so the action has to refuse an empty field — and AC-9 requires that refusal to be
   * **announced**, which a disabled button is not. So there is a sentence, and it says only what happened.
   *
   * ⛔ **It is not a replacement for anything omitted.** The two visible strings that story drops — the
   * suggestion helper and *"You can rename it later from the Admin."* — are left with nothing in their
   * place, deliberately [G11, G12].
   */
  nameRequired: 'Enter a name for this kiosk.',
  /**
   * ⭐ **What a screen-reader user is told while the boot probe is with the server** [03-us-kad-6i AC-11;
   * eval row **O1b**]. The interim state draws one element and it is a `role="presentation"` spinner
   * [`0307`], so this sentence is the only thing that reaches assistive technology at all.
   *
   * ⛔ **It claims NO enrolment outcome** — not enrolled, not locked, nothing about a PIN [`0262`'s posture
   * at this entry point] — and ⛔ **`ENROLMENT_COPY.connecting*` is not reused**: frame `06` is drawn for
   * *six digits are with the server*, and on a power-cycle nobody connected anything.
   */
  checkingEnrolment: 'Checking this device…',
  /**
   * ⭐ **The one way back from a boot that got no answer** [03-us-kad-6i AC-10; eval row **B6c**], on the
   * pad and nowhere else — it renders only while this load's probe has claimed nothing.
   *
   * ⛔ **A BEHAVIOUR the criterion requires, whose wording nobody has ruled.** AC-10 says the affordance
   * does not ride on gap **G2**, so the control ships and the string sits here: it says what pressing it
   * does and ⛔ claims no enrolment outcome — not that the device is enrolled, not that it is not, and
   * nothing about a PIN. ⛔ **`RULED_COPY` is not borrowed for it**: those strings were ruled for
   * `POST /enrolment` and the layer-1 token call, and extending one to a new entry point by inference is
   * what AC-10 forbids in terms.
   */
  bootRetry: 'Try again',
} as const;
